/**
 * The only module that fetches plant content.
 *
 * The catalogue is served as static JSON from hosting rather than bundled, for
 * the reason `src/domain/i18n/messages/index.js` gives: the shell's vocabulary
 * is a few hundred bytes and must never flash, while plant content is the
 * payload actually worth splitting.
 *
 * Two files per load, by design (SPEC-002 D-6): properties are
 * language-independent and cached once across a locale switch, prose is not.
 */

const CONTENT_BASE = '/assets/content/plants';
const REQUEST_TIMEOUT_MS = 10000;

/**
 * `fetch` and `AbortController` arrive as defaulted parameters so this is
 * testable without a network and safe to import outside a browser.
 */
async function fetchJson(url, { fetchImpl = globalThis.fetch, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(url, { signal: controller.signal });

        if (!response.ok) {
            throw new Error(`${url} responded ${response.status}`);
        }

        return await response.json();
    } finally {
        clearTimeout(timer);
    }
}

export async function fetchCatalogue(options = {}) {
    const payload = await fetchJson(`${CONTENT_BASE}/catalogue.json`, options);

    return payload.plants || [];
}

/**
 * Prose for a language, and English underneath it.
 *
 * `en` and `hu` are authored; `de` 404s until it is translated. That is an
 * expected state, not an error — a missing translation shows English copy, never
 * an empty card — so a failed language fetch resolves to `{}` and lets the
 * English layer show through.
 */
export async function fetchProse(languageCode, options = {}) {
    const english = await fetchJson(`${CONTENT_BASE}/prose.en.json`, options);

    if (languageCode === 'en') {
        return { prose: english, fallbackProse: english };
    }

    try {
        return { prose: await fetchJson(`${CONTENT_BASE}/prose.${languageCode}.json`, options), fallbackProse: english };
    } catch {
        return { prose: {}, fallbackProse: english };
    }
}

export { CONTENT_BASE };
